<template>
  <div id="instructions-menu">
    <div 
      id="tactical-style-label" 
      class="--uppercase">
        {{ $t('ui.tacticalStyle') }}
    </div>
    <div id="tactical-style">{{ selectedPlayingStyle }}</div>
    
    <label id="mentality-label" class="--uppercase" for="mentality">
      {{ $t('tactics.mentality.mentality') }}
    </label>
    <select
      class="ti-select instructions-menu-item --capitalise" 
      name="mentality"
      id="mentality-select"
      v-model="mentality">
      <option
        v-for="mentality in mentalities"
        :key="mentality.string"
        :value="mentality.value"
        :selected="mentality.selected">
          {{ $t(mentality.string) }}
      </option>
    </select>

    <div id="instruction-toggle-container">
      <div 
        id="in-instructions-container"
        class="instructions-container">
        <ButtonToggleInstructionsMenu
          :selected="show.inPossession"
          :appliedInstructions="appliedInstructionsInPossession"
          @show="showPanel('inPossession')">
            {{ $t('tactics.possession.inPossession') }}
        </ButtonToggleInstructionsMenu>
        <transition name="slide" appear>
          <TheTeamInstructionsInPossession v-if="show.inPossession" />
        </transition>
      </div>

      <div 
        id="transition-instructions-container"
        class="instructions-container">
        <ButtonToggleInstructionsMenu
          :selected="show.inTransition"
          :appliedInstructions="appliedInstructionsInTransition"
          @show="showPanel('inTransition')">
            {{ $t('tactics.possession.inTransition') }}
        </ButtonToggleInstructionsMenu>
        <transition name="slide">
          <TheTeamInstructionsInTransition v-if="show.inTransition" />
        </transition>
      </div>

      <div
        id="out-instructions-container"
        class="instructions-container">
        <ButtonToggleInstructionsMenu
          :selected="show.outOfPossession"
          :appliedInstructions="appliedInstructionsOutOfPossession"
          @show="showPanel('outOfPossession')">
            {{ $t('tactics.possession.outOfPossession') }}
        </ButtonToggleInstructionsMenu>
        <transition name="slide">
          <TheTeamInstructionsOutOfPossession v-if="show.outOfPossession" />
        </transition>
      </div>
    </div>

    <TeamAttributeLoader />
  </div>
</template>

<script>
import ButtonToggleInstructionsMenu from "@/components/ButtonToggleInstructionsMenu";
import TheTeamInstructionsInPossession from './TheTeamInstructionsInPossession';
import TheTeamInstructionsInTransition from './TheTeamInstructionsInTransition';
import TheTeamInstructionsOutOfPossession from './TheTeamInstructionsOutOfPossession';
import TeamAttributeLoader from "@/components/TeamAttributeLoader";


export default {
  components: {
    ButtonToggleInstructionsMenu,
    TheTeamInstructionsInPossession,
    TheTeamInstructionsInTransition,
    TheTeamInstructionsOutOfPossession,
    TeamAttributeLoader,
  },

  data: function() {
    return {
      mentalities: [
        { value: -2, string: 'tactics.mentality.defensive' },
        { value: -1, string: 'tactics.mentality.cautious' },
        { value: 0, string: 'tactics.mentality.balanced' },
        { value: 1, string: 'tactics.mentality.positive' },
        { value: 2, string: 'tactics.mentality.attacking' }
      ],
      show: {
        inPossession: false,
        inTransition: false,
        outOfPossession: false
      },
      changeString: '>> ' + this.$t('ui.change'),
      doneString: '<< ' + this.$t('ui.done'),
      production: process.env.NODE_ENV !== "development",
    };
  },

  computed: {
    mentality: {
      get () {
        return this.$store.state.instructions.mentality;
      },
      set (value) {
        this.$store.commit("updateMentality", value);
      }
    },

    selectedPlayingStyle: function() {
      return this.$t(this.$store.getters.playingStyleNameAddress);
    },

    appliedInstructionsInPossession: function() {
      const inPossession = this.$store.state.instructions.inPossession;
      let instructions = [];

      if (inPossession.attackingWidth.attackingWidth !== 0) {
        const rootAddress = "tactics.instructions.inPossession.attackingWidth.",
            addresses = ["extremelyNarrow", "narrow", "", "wide", "extremelyWide"];
        instructions.push(rootAddress + addresses[inPossession.attackingWidth.attackingWidth+2]);
      }

      if (inPossession.approachPlay.passIntoSpace) instructions.push("tactics.instructions.inPossession.approachPlay.passIntoSpace");
      if (inPossession.approachPlay.overlapLeft !== 0) {
        const rootAddress = "tactics.instructions.inPossession.approachPlay.",
            addresses = ["underlapLeft", "", "overlapLeft"];
        instructions.push(rootAddress + addresses[inPossession.approachPlay.overlapLeft+1]);
      }
      if (inPossession.approachPlay.overlapRight !== 0) {
        const rootAddress = "tactics.instructions.inPossession.approachPlay.",
            addresses = ["underlapRight", "", "overlapRight"];
        instructions.push(rootAddress + addresses[inPossession.approachPlay.overlapRight+1]);
      }
      if (inPossession.approachPlay.playOutOfDefence) instructions.push("tactics.instructions.inPossession.approachPlay.playOutOfDefence");

      if (inPossession.passing !== 0) {
        const rootAddress = "tactics.instructions.inPossession.passing.",
            addresses = ["muchShorter", "shorter", "", "moreDirect", "muchMoreDirect"];
        instructions.push(rootAddress + addresses[inPossession.passing+2]);
      }
      if (inPossession.tempo !== 0) {
        const rootAddress = "tactics.instructions.inPossession.tempo.",
            addresses = ["muchLower", "lower", "", "higher", "muchHigher"];
        instructions.push(rootAddress + addresses[inPossession.tempo+2]);
      }
      if (inPossession.timeWasting !== 0) {
        const rootAddress = "tactics.instructions.inPossession.timeWasting.",
            addresses = ["frequently", "sometimes"];
        instructions.push(rootAddress + addresses[inPossession.timeWasting+2]);
      }

      if (inPossession.finalThird.workBallIntoBox) instructions.push("tactics.instructions.inPossession.finalThird.workBallIntoBox");
      if (inPossession.finalThird.shootOnSight) instructions.push("tactics.instructions.inPossession.finalThird.shootOnSight");
      if (inPossession.finalThird.hitEarlyCrosses) instructions.push("tactics.instructions.inPossession.finalThird.hitEarlyCrosses");
      if (inPossession.finalThird.playForSetPieces) instructions.push("tactics.instructions.inPossession.finalThird.playForSetPieces");

      if (inPossession.dribbling !== 0) {
        const rootAddress = "tactics.instructions.inPossession.dribbling.",
            addresses = ["less", "", "more"];
        instructions.push(rootAddress + addresses[inPossession.dribbling+1]);
      }
      if (inPossession.creativeFreedom !== 0) {
        const rootAddress = "tactics.instructions.inPossession.creativeFreedom.",
            addresses = ["less", "", "more"];
        instructions.push(rootAddress + addresses[inPossession.creativeFreedom+1]);
      }
      return instructions;
    },

    appliedInstructionsInTransition: function() {
      const inTransition = this.$store.state.instructions.inTransition;
      let instructions = [];
      if (inTransition.attackingTransition > 0) instructions.push('tactics.instructions.transitions.attacking.counter');
      if (inTransition.attackingTransition < 0) instructions.push('tactics.instructions.transitions.attacking.holdShape');
      if (inTransition.defensiveTransition > 0) instructions.push('tactics.instructions.transitions.defensive.counterPress');
      if (inTransition.defensiveTransition < 0) instructions.push("tactics.instructions.transitions.defensive.regroup");
      if (inTransition.goalkeeperInPossession < 0) instructions.push("tactics.instructions.transitions.goalkeeper.slowPaceDown");
      if (inTransition.goalkeeperInPossession > 0) instructions.push("tactics.instructions.transitions.goalkeeper.distributeQuickly");
      return instructions;
    },

    appliedInstructionsOutOfPossession: function() {
      const outOfPossession = this.$store.state.instructions.outOfPossession;
      let instructions = [];

      if (outOfPossession.defensiveShape.useOffsideTrap !== 0) {
        const rootAddress = "tactics.instructions.outOfPossession.steppingUp.",
            addresses = ["dropOffMore", "", "stepUpMore"];
        instructions.push(rootAddress + addresses[outOfPossession.defensiveShape.useOffsideTrap+1]);
      }
      if (outOfPossession.defensiveShape.engagementLine !== 0) {
        const rootAddress = "tactics.instructions.outOfPossession.defensiveShape.lineOfEngagement.",
            addresses = ["muchLower", "lower", "", "higher", "muchHigher"];
        instructions.push(rootAddress + addresses[outOfPossession.defensiveShape.engagementLine+2]);
      }
      if (outOfPossession.defensiveShape.defensiveLine !== 0) {
        const rootAddress = "tactics.instructions.outOfPossession.defensiveShape.defensiveLine.",
            addresses = ["muchLower", "lower", "", "higher", "muchHigher"];
        instructions.push(rootAddress + addresses[outOfPossession.defensiveShape.defensiveLine+2]);
      }
      if (outOfPossession.defensiveShape.defensiveWidth !== 0) {
        const rootAddress = "tactics.instructions.outOfPossession.defensiveShape.defensiveWidth.",
            addresses = ["narrower", "", "wider"];
        instructions.push(rootAddress + addresses[outOfPossession.defensiveShape.defensiveWidth+1]);
      }

      if (outOfPossession.markingAndTackling.useTighterMarking) instructions.push("tactics.instructions.outOfPossession.markingAndTackling.tighterMarking");
      if (outOfPossession.markingAndTackling.pressingUrgency !== 0) {
        const rootAddress = "tactics.instructions.outOfPossession.markingAndTackling.pressing.",
            addresses = ["muchLess", "less", "", "more", "muchMore"];
        instructions.push(rootAddress + addresses[outOfPossession.markingAndTackling.pressingUrgency+2]);
      }
      if (outOfPossession.markingAndTackling.preventGKDistribution) instructions.push("tactics.instructions.outOfPossession.markingAndTackling.preventShortGKDistribution");
      if (outOfPossession.markingAndTackling.tackling !== 0) {
        const rootAddress = "tactics.instructions.outOfPossession.markingAndTackling.tackling.",
            addresses = ["stayOnFeet", "", "getStuckIn"];
        instructions.push(rootAddress + addresses[outOfPossession.markingAndTackling.tackling+1]);
      }
      if (outOfPossession.pressingTrap < 0) instructions.push("tactics.instructions.outOfPossession.pressingTrap.outside");
      if (outOfPossession.pressingTrap > 0) instructions.push("tactics.instructions.outOfPossession.pressingTrap.inside");
      if (outOfPossession.crossEngagement < 0) instructions.push("tactics.instructions.outOfPossession.crossEngagement.invite");
      if (outOfPossession.crossEngagement > 0) instructions.push("tactics.instructions.outOfPossession.crossEngagement.stop");

      return instructions;
    }
  },

  methods: {
    showPanel: function(panel) {
      Object.keys(this.show).forEach(key => {
        this.show[key] = (panel === key ? !this.show[key]: false);
      });
    }
  }
}
</script>

<style scoped>
.instr-panel-toggle {
  box-sizing: border-box;
  border: solid 1px rgba(0,0,0,0);
}

.slide-enter-active, .slide-leave-active {
  transition: all .4s ease;
}

.slide-enter, .slide-leave-to {
  transform: translateX(-100%);
}

#mentality-select {
  border: solid 1px rgba(0,0,0,0);
}

@media only screen and (max-width: 767px) {
  .mobile-hide {
    display: none !important;
  }

  #instructions-menu {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    box-sizing: border-box;
    max-width: 100vw;
  }

  #tactical-style-label, #mentality-label {
    min-width: 34%;
    margin-right: auto;
  }

  #tactical-style, #mentality-select {
    min-width: 34%;
    margin-left: auto;
    width: auto;
  }

  #tactical-style {
    margin-bottom: initial;
    border-bottom: none;
    font-size: small;
    font-weight: unset;
  }

  #tactical-style-label, #mentality-label {
    text-align: left;
    margin-left: 12px;
    font-size: 12px;
    color: #98B9A8;
    font-weight: 500;
  }

  #instruction-toggle-container {
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 4px;
    justify-content: space-between;
    max-width: 100%;
  }
  
  .instructions-container {
    min-width: 0;
  }
}

</style>
